import { z } from "zod";
/**
 * BE-10 — signalements : cycle de vie, timeline et coffre de preuves.
 *
 * Ce fichier est LA frontière entre le vocabulaire de la base et celui du
 * produit. La base garde ses quatre statuts historiques (`OPEN`, `REVIEWED`,
 * `ACTIONED`, `DISMISSED`) plus `NEEDS_INFO` ; le produit, lui, parle de
 * « reçu », « en cours d'examen », « complément demandé », « décidé ». Le
 * mapping vit ici, en fonctions pures, testables sans base.
 *
 * Pourquoi ne pas avoir renommé les valeurs en base : pendant un rolling deploy
 * l'instance N-1 sert encore et filtre sa file d'attente sur `?status=OPEN`.
 * Une valeur qu'elle ignore rendrait invisible tout signalement déposé pendant
 * le deploy — et c'est cette file qui tient le SLA de 24 h.
 */
// ── Type de dossier (v13.7) ──────────────────────────────────────────────────
/**
 * Nature d'un DOSSIER (enum Prisma `ReportType`). Un ticket est un dossier de
 * modération à l'OBJET près : REPORT_CONTENT = signalement de contenu (le seul
 * cas historique), BUG / FEATURE = les deux tickets. Le discriminateur porte
 * l'unique différence de comportement — états terminaux, gate de notification,
 * cascade RGPD ; tout le reste (timeline, page admin, suivi auteur) est partagé.
 */
export const ReportTypeSchema = z.enum(["REPORT_CONTENT", "BUG", "FEATURE", "SUPPORT"]);
/** Les deux types de TICKET (par opposition au signalement de contenu). */
export const TicketTypeSchema = z.enum(["BUG", "FEATURE"]);
/** Un dossier est-il un ticket (BUG/FEATURE) plutôt qu'un signalement ? */
export function isTicketType(type) {
    return type === "BUG" || type === "FEATURE";
}
// ── Statuts ─────────────────────────────────────────────────────────────────
/**
 * Statuts tels qu'ils sont STOCKÉS (enum Prisma `ReportStatus`).
 *
 * v13.7 — RESOLVED/PLANNED/DECLINED sont les statuts TERMINAUX des tickets, dans
 * le même enum : un ticket vit dans la même table Report, avec le même chemin
 * d'écriture du statut (services/reportLifecycle.applyReportTransition). Le
 * mapping stocké↔produit est l'IDENTITÉ pour ces trois (comme NEEDS_INFO) :
 * inutile de renommer une valeur que le produit nomme déjà comme la base.
 */
export const ReportStoredStatusSchema = z.enum([
    "OPEN",
    "REVIEWED",
    "NEEDS_INFO",
    "ACTIONED",
    "DISMISSED",
    "RESOLVED",
    "PLANNED",
    "DECLINED",
]);
/** Statuts tels qu'ils sont NOMMÉS côté produit (et servis par l'API). */
export const ReportProductStatusSchema = z.enum([
    "RECEIVED",
    "IN_REVIEW",
    "NEEDS_INFO",
    "ACTIONED",
    "DISMISSED",
    "RESOLVED",
    "PLANNED",
    "DECLINED",
]);
const STORED_TO_PRODUCT = {
    OPEN: "RECEIVED",
    REVIEWED: "IN_REVIEW",
    NEEDS_INFO: "NEEDS_INFO",
    ACTIONED: "ACTIONED",
    DISMISSED: "DISMISSED",
    RESOLVED: "RESOLVED",
    PLANNED: "PLANNED",
    DECLINED: "DECLINED",
};
const PRODUCT_TO_STORED = {
    RECEIVED: "OPEN",
    IN_REVIEW: "REVIEWED",
    NEEDS_INFO: "NEEDS_INFO",
    ACTIONED: "ACTIONED",
    DISMISSED: "DISMISSED",
    RESOLVED: "RESOLVED",
    PLANNED: "PLANNED",
    DECLINED: "DECLINED",
};
/** Statut stocké → statut produit. Tolère une valeur inconnue (→ RECEIVED). */
export function reportProductStatus(stored) {
    return STORED_TO_PRODUCT[stored] ?? "RECEIVED";
}
/** Statut produit → statut stocké (filtres de la file admin, transitions). */
export function reportStoredStatus(product) {
    return PRODUCT_TO_STORED[product] ?? null;
}
/**
 * Un dossier TERMINÉ : plus rien n'est attendu de l'auteur ni de l'admin.
 *
 * v13.7 — RESOLVED et DECLINED closent un ticket comme ACTIONED/DISMISSED closent
 * un signalement. PLANNED est une DÉCISION (motif public rendu) mais PAS un état
 * terminal : un ticket planifié peut encore être livré (RESOLVED) ou abandonné
 * (DECLINED), il reste donc « ouvert » au sens de cette fonction.
 */
export function isReportClosed(product) {
    return (product === "ACTIONED" ||
        product === "DISMISSED" ||
        product === "RESOLVED" ||
        product === "DECLINED");
}
// ── Timeline ────────────────────────────────────────────────────────────────
/** Nature d'un événement (enum Prisma `ReportEventKind`). */
export const ReportEventKindSchema = z.enum([
    "RECEIVED",
    "IN_REVIEW",
    "NEEDS_INFO",
    "ACTIONED",
    "DISMISSED",
    "WARNED",
    "BANNED",
    "REOPENED",
    "ASSIGNED",
    "NOTE",
    "VAULT_ACCESSED",
    "VAULT_PURGED",
    // ADMIN-14 (EPIC v13.11) — message VISIBLE de l'admin au déclarant. Neutre côté
    // statut (ne change pas l'état du dossier), mais SERVI à /reports/mine et notifié
    // (contrairement à NOTE, interne). Le corps du message vit dans `publicReason`.
    "ADMIN_MESSAGE",
    // MOD-2 (v13.12) — demande d'informations envoyée par E-MAIL. Neutre côté
    // statut, et surtout PAS servie à /reports/mine : elle est partie hors de
    // l'application. Le corps vit dans `internalNote` (trace de ce qui a été
    // écrit), jamais renvoyé au déclarant par l'API.
    "ADMIN_EMAIL",
    // MOD-1 (v13.12) — RÉPONSE DU DÉCLARANT. Seul kind écrit par l'utilisateur et
    // non par un modérateur. Neutre côté statut ; le texte vit dans `publicReason`
    // (c'est le déclarant qui l'a écrit, il a le droit de le relire).
    "USER_REPLY",
    // v13.7 — DÉCISIONS de TICKET. Homonymes de leur statut résultant, comme
    // ACTIONED/DISMISSED côté modération.
    "RESOLVED",
    "PLANNED",
    "DECLINED",
]);
/**
 * Statut résultant d'un événement, ou `null` quand l'événement ne change rien
 * (assignation, note interne, accès au coffre, purge du coffre).
 */
export function reportEventResultStatus(kind) {
    switch (kind) {
        case "RECEIVED":
        // Réouvrir, c'est REMETTRE DANS LA FILE — pas se déclarer en train
        // d'examiner. Le dossier repasse donc « reçu », et un admin devra le
        // reprendre explicitement. C'est aussi ce qui préserve le contrat de
        // l'endpoint historique `/resolve` avec `status: "OPEN"`, qui attend
        // exactement ce statut et un `resolvedAt` remis à null.
        case "REOPENED":
            return "RECEIVED";
        case "IN_REVIEW":
            return "IN_REVIEW";
        case "NEEDS_INFO":
            return "NEEDS_INFO";
        // Avertir et bannir SONT des décisions : elles tranchent le dossier. Le
        // déclarant n'apprend jamais laquelle des deux a été appliquée — il voit
        // « action prise » et le motif public, rien de plus.
        case "ACTIONED":
        case "WARNED":
        case "BANNED":
            return "ACTIONED";
        case "DISMISSED":
            return "DISMISSED";
        // v13.7 — décisions de ticket, homonymes de leur statut résultant.
        case "RESOLVED":
            return "RESOLVED";
        case "PLANNED":
            return "PLANNED";
        case "DECLINED":
            return "DECLINED";
        default:
            return null;
    }
}
/** Les événements dont le déclarant est prévenu (WS + push, motif public seul). */
export function reportEventNotifiesReporter(kind) {
    // ADMIN-14 — le message admin notifie l'auteur bien qu'il ne change pas le statut.
    if (kind === "ADMIN_MESSAGE")
        return true;
    // MOD-2 — l'e-mail est DÉJÀ le canal : notifier en plus dans l'app ferait
    // recevoir deux fois la même demande, dont une sans le texte.
    if (kind === "ADMIN_EMAIL")
        return false;
    // MOD-1 — c'est le déclarant lui-même qui écrit : se notifier soi-même n'a
    // aucun sens (et ferait sonner son téléphone en tapant).
    if (kind === "USER_REPLY")
        return false;
    return reportEventResultStatus(kind) !== null && kind !== "RECEIVED";
}
/**
 * Machine à états — DEUX pistes, un seul moteur.
 *
 * Une transition absente de la piste du dossier est refusée (409) : le statut
 * courant est une projection de la timeline, pas un champ qu'on écrase. Décider
 * directement depuis « reçu » est volontairement permis des deux côtés (le SLA
 * modération est de 24 h ; un ticket évident ne doit pas exiger deux clics).
 *
 * v13.7 — la piste dépend du TYPE du dossier, et c'est ce qui garde « un seul
 * chemin de code » sûr : `applyReportTransition` reste unique, mais un `kind` de
 * modération (ACTIONED, BANNED…) posé sur un TICKET, ou une décision de ticket
 * (RESOLVED…) posée sur un SIGNALEMENT, tombe dans une table qui n'en veut pas et
 * se fait refuser. La table est `Partial` : une clé absente = aucune transition
 * de statut permise depuis cet état (les kinds sans effet de statut restent
 * admis partout, voir plus bas).
 */
const MODERATION_TRANSITIONS = {
    RECEIVED: ["IN_REVIEW", "NEEDS_INFO", "ACTIONED", "WARNED", "BANNED", "DISMISSED"],
    // `REOPENED` depuis un état NON tranché = « remettre dans la file ». Ce n'est
    // pas décoratif : c'est exactement ce que fait l'endpoint historique
    // `POST /admin/reports/:id/resolve {status:"OPEN"}` sur un dossier `REVIEWED`,
    // et le refuser (409) cassait un geste que le client N-1 pouvait faire. Depuis
    // `RECEIVED`, en revanche, il n'y a rien à remettre dans la file : le handler
    // legacy court-circuite (statut déjà atteint) et l'écran FE-4 ne propose la
    // réouverture que sur un dossier tranché.
    IN_REVIEW: ["NEEDS_INFO", "ACTIONED", "WARNED", "BANNED", "DISMISSED", "REOPENED"],
    NEEDS_INFO: ["IN_REVIEW", "ACTIONED", "WARNED", "BANNED", "DISMISSED", "REOPENED"],
    ACTIONED: ["REOPENED"],
    DISMISSED: ["REOPENED"],
};
/**
 * Piste TICKET (BUG/FEATURE). Trois issues (RESOLVED | PLANNED | DECLINED). Un
 * ticket PLANIFIÉ n'est pas terminal : il peut être livré (RESOLVED) ou abandonné
 * (DECLINED) plus tard sans repasser par la file. NEEDS_INFO/WARNED/BANNED
 * n'existent pas ici — on ne bannit pas l'auteur d'un rapport de bug.
 */
const TICKET_TRANSITIONS = {
    RECEIVED: ["IN_REVIEW", "RESOLVED", "PLANNED", "DECLINED"],
    IN_REVIEW: ["RESOLVED", "PLANNED", "DECLINED", "REOPENED"],
    PLANNED: ["RESOLVED", "DECLINED", "REOPENED"],
    RESOLVED: ["REOPENED"],
    DECLINED: ["REOPENED"],
};
/**
 * ADMIN-10 (EPIC v13.11) — piste DEMANDE USER (type SUPPORT). Deux issues
 * (RESOLVED | DECLINED), pas de PLANNED (pas de « planifié » pour du support) et
 * surtout pas de BANNED/WARNED (on ne bannit pas quelqu'un qui demande de l'aide).
 */
const SUPPORT_TRANSITIONS = {
    RECEIVED: ["IN_REVIEW", "RESOLVED", "DECLINED"],
    IN_REVIEW: ["RESOLVED", "DECLINED", "REOPENED"],
    RESOLVED: ["REOPENED"],
    DECLINED: ["REOPENED"],
};
/**
 * ADMIN-8 (EPIC v13.11) — REGISTRE des pistes de transition, une entrée par
 * `ReportType`. Remplace le ternaire binaire (`type === "REPORT_CONTENT" ?
 * MODERATION : TICKET`) où tout 3ᵉ flux retombait EN SILENCE sur la piste TICKET.
 *
 * `Record<ReportType, …>` : ajouter un `ReportType` à l'enum SANS l'enregistrer
 * ici est une ERREUR de compilation — le moteur force la déclaration d'une table
 * par flux. Un type inconnu au RUNTIME (valeur hors enum lue en base) est REFUSÉ
 * par `isReportTransitionAllowed`, jamais rabattu sur une piste par défaut.
 *
 * Enregistrer un nouveau flux = ajouter sa table ici (+ ses `ReportEventKind` au
 * `reportEventResultStatus`), sans toucher aucune branche existante (ADMIN-10
 * ajoutera SUPPORT ainsi).
 */
export const REPORT_TRANSITION_TABLES = {
    REPORT_CONTENT: MODERATION_TRANSITIONS,
    BUG: TICKET_TRANSITIONS,
    FEATURE: TICKET_TRANSITIONS,
    SUPPORT: SUPPORT_TRANSITIONS,
};
/** Vrai si `kind` est applicable depuis `from` sur un dossier de ce `type`. Les
 *  kinds sans effet de statut (note, assignation, accès au coffre) sont admis
 *  partout. Défaut REPORT_CONTENT : les appelants historiques restent corrects.
 *  Un flux non enregistré ⇒ refus (jamais de repli silencieux sur TICKET). */
export function isReportTransitionAllowed(from, kind, type = "REPORT_CONTENT") {
    if (reportEventResultStatus(kind) === null)
        return true;
    const table = REPORT_TRANSITION_TABLES[type];
    if (!table)
        return false;
    return table[from]?.includes(kind) ?? false;
}
/**
 * ADMIN-13 (EPIC v13.11) — kinds de DÉCISION applicables depuis `from` pour ce
 * `type`. La console en DÉRIVE ses boutons d'action (elle interroge le moteur pour
 * l'état courant plutôt que de coder en dur des boutons par écran) : un flux non
 * enregistré ⇒ aucune action, et une décision hors table est refusée (409) côté
 * serveur. Les kinds neutres (note, assignation, accès coffre) ne sont PAS des
 * boutons de transition — ils vivent ailleurs dans l'écran.
 */
export function allowedReportTransitions(from, type = "REPORT_CONTENT") {
    return REPORT_TRANSITION_TABLES[type]?.[from] ?? [];
}
/**
 * ADMIN-13 — libellé PRODUIT d'une décision. Signalements et bugs sont FUSIONNÉS :
 * le vocabulaire « Go / No-Go » (agir / classer) recouvre les kinds de modération,
 * les décisions de ticket gardent leur mot propre (résolu/planifié/refusé).
 */
export function reportDecisionLabel(kind) {
    switch (kind) {
        case "IN_REVIEW":
            return "Prendre en charge";
        case "NEEDS_INFO":
            return "Demander un complément";
        case "ACTIONED":
            return "Go — action prise";
        case "WARNED":
            return "Go — avertir";
        case "BANNED":
            return "Go — bannir";
        case "DISMISSED":
            return "No-Go — classer sans suite";
        case "RESOLVED":
            return "Résolu";
        case "PLANNED":
            return "Planifié";
        case "DECLINED":
            return "Refusé";
        case "REOPENED":
            return "Rouvrir";
        default:
            return kind;
    }
}
// ── Référence courte ───────────────────────────────────────────────────────
/**
 * Référence lisible d'un signalement : `#` + les 4 premiers caractères de l'id
 * (FE-3). Volontairement pas l'UUID complet — ce qu'on veut, c'est quelque
 * chose qu'un utilisateur puisse recopier dans un e-mail, et qui ne serve pas
 * de clé (elle n'est pas unique, elle est humaine).
 */
export function reportReference(id) {
    return `#${id.replace(/-/g, "").slice(0, 4).toUpperCase()}`;
}
// ── Corps de requêtes ──────────────────────────────────────────────────────
/**
 * Kinds qu'un admin peut poser à la main (le reste est posé par le serveur).
 *
 * v13.7 — RESOLVED/PLANNED/DECLINED s'ajoutent pour les décisions de TICKET.
 * L'endpoint admin est unique (POST /admin/reports/:id/events) ; c'est la machine
 * à états type-aware (isReportTransitionAllowed) qui refuse un kind de la mauvaise
 * piste — un RESOLVED sur un signalement de contenu, un BANNED sur un ticket.
 */
export const ReportAdminEventKindSchema = z.enum([
    "IN_REVIEW",
    "NEEDS_INFO",
    "ACTIONED",
    "DISMISSED",
    "WARNED",
    "BANNED",
    "REOPENED",
    "NOTE",
    "ADMIN_MESSAGE",
    "ADMIN_EMAIL",
    "RESOLVED",
    "PLANNED",
    "DECLINED",
]);
export const MAX_REPORT_REASON_LENGTH = 1000;
export const MAX_REPORT_NOTE_LENGTH = 4000;
/**
 * `POST /v1/admin/reports/:id/events` — transition + MOTIF OBLIGATOIRE.
 *
 * Le motif n'est pas décoratif : c'est ce que le déclarant lira, et c'est la
 * seule trace qui restera si un tiers demande un jour pourquoi telle décision a
 * été prise. Une note interne seule ne suffit donc pas pour une décision.
 */
export const CreateReportEventSchema = z
    .object({
    kind: ReportAdminEventKindSchema,
    /** Motif communiqué au déclarant. Jamais la sanction, jamais le modérateur. */
    publicReason: z.string().trim().min(5).max(MAX_REPORT_REASON_LENGTH).optional(),
    /** Note interne — jamais servie au déclarant. */
    internalNote: z.string().trim().min(1).max(MAX_REPORT_NOTE_LENGTH).optional(),
})
    .superRefine((data, ctx) => {
    if (data.kind === "NOTE") {
        if (!data.internalNote) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "internalNote is required for a NOTE",
                path: ["internalNote"],
            });
        }
        return;
    }
    if (!data.publicReason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "publicReason is required for any decision",
            path: ["publicReason"],
        });
    }
});
/** Assignation d'un dossier. `adminId: null` = se dessaisir. */
export const AssignReportSchema = z.object({
    adminId: z.string().uuid().nullable(),
    internalNote: z.string().trim().max(MAX_REPORT_NOTE_LENGTH).optional(),
});
// ── Réponses (le contrat que le client peut supposer) ──────────────────────
/** Une étape de la timeline TELLE QUE LA VOIT LE DÉCLARANT : statut, date,
 *  motif public. Aucune note interne, aucun nom de modérateur. */
export const MyReportTimelineStepSchema = z.object({
    status: ReportProductStatusSchema,
    at: z.string(),
    publicReason: z.string().nullable(),
    /**
     * MOD-1 (correctif UX du 2 août) — NATURE de la ligne, pour que le client
     * distingue une ÉTAPE (le dossier avance) d'un MESSAGE (quelqu'un écrit).
     *
     * Sans ce champ, l'app affichait tout en vrac : un mot du modérateur et un
     * changement de statut se ressemblaient, et la réponse de l'utilisateur se
     * perdait au milieu. Optionnel : un serveur N-1 ne le sert pas, le client
     * retombe alors sur l'affichage en liste d'étapes.
     */
    kind: z.enum(["step", "adminMessage", "userReply"]).optional(),
});
export const MyReportSchema = z.object({
    id: z.string(),
    reference: z.string(),
    status: ReportProductStatusSchema,
    reason: z.string(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    target: z.object({
        kind: z.enum(["user", "group", "none"]),
        label: z.string().nullable(),
    }),
    timeline: z.array(MyReportTimelineStepSchema),
    /**
     * MOD-1 — le modérateur a-t-il ouvert le fil aux réponses ? Le client n'affiche
     * un champ de réponse QUE si c'est vrai. Optionnel : un serveur N-1 ne le sert
     * pas, et le client doit alors se comporter comme avant (aucun champ).
     */
    replyOpen: z.boolean().optional(),
});
/**
 * MOD-5 — CE QU'IL FAUT POUR AFFICHER « ÉQUIPE LIVEMESSAGE » DANS LA LISTE DES
 * CONVERSATIONS, sans charger tous les dossiers.
 *
 * La liste des conversations se recharge souvent (au retour d'un écran, à chaque
 * rafraîchissement) : lui faire tirer 100 dossiers avec leurs timelines pour
 * afficher UNE ligne serait absurde. Ce résumé tient en une requête et se lit
 * comme une conversation : « qui a parlé en dernier, quand, et est-ce lu ».
 */
export const MyReportsSummarySchema = z.object({
    /** Nombre de dossiers ouverts par ce compte. 0 ⇒ aucune ligne à afficher. */
    total: z.number(),
    /** Dossiers où l'équipe a écrit sans que l'utilisateur ait rouvert son suivi. */
    unread: z.number(),
    /** Aperçu du dernier message de l'équipe (déjà borné côté serveur). */
    lastMessage: z.string().nullable(),
    /** Date de la dernière activité visible par l'utilisateur (ISO), s'il y en a. */
    lastAt: z.string().nullable(),
});
//# sourceMappingURL=reports.js.map